import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { 
  Wrench, 
  Shield, 
  Settings,
  ArrowLeft,
  ArrowRight,
  Check,
  Upload,
  Building2,
  MapPin,
  Clock,
  DollarSign,
  Star,
  Phone,
  Mail,
  MessageSquare
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const SKILLS_OPTIONS = [
  "Cleaning",
  "Painting",
  "Landscaping / Lawn Care",
  "HVAC",
  "Plumbing",
  "Electrical",
  "Roofing",
  "Handyman / General Repairs",
  "Flooring",
  "Drywall",
  "Tile",
  "Pressure Washing",
  "Appliance Repair",
  "Pest Control",
  "Other"
];

const EXPERIENCE_OPTIONS = [
  "0–1 years",
  "2–5 years",
  "6–10 years",
  "10+ years"
];

const LICENSE_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
  { value: "Not required", label: "Not required for my trade" }
];

const BUSINESS_TYPE_OPTIONS = [
  "Solo contractor",
  "Small team (2–5)",
  "Company (6+)"
];

const JOB_SIZE_OPTIONS = [
  "Less than $1,000",
  "$1,000–$5,000",
  "$5,000–$10,000",
  "$10,000+"
];

const AVAILABILITY_OPTIONS = [
  "Same-day / Emergency",
  "Within 1–3 days",
  "Within a week",
  "Scheduled / non-urgent only"
];

const REVIEW_PLATFORM_OPTIONS = [
  "Google",
  "Yelp",
  "Angi",
  "Facebook",
  "No online reviews yet"
];

const CONTACT_METHOD_OPTIONS = [
  { value: "Phone", icon: Phone },
  { value: "Text", icon: MessageSquare },
  { value: "Email", icon: Mail }
];

export default function ContractorOnboarding() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Page 1 state
  const [skills, setSkills] = useState([]);
  const [otherSkillDescription, setOtherSkillDescription] = useState("");
  const [yearsExperience, setYearsExperience] = useState("");
  
  // Page 2 state
  const [isLicensed, setIsLicensed] = useState("");
  const [isInsured, setIsInsured] = useState(false);
  const [insuranceProofUrl, setInsuranceProofUrl] = useState("");
  const [serviceArea, setServiceArea] = useState("");
  const [businessType, setBusinessType] = useState("");
  
  // Page 3 state
  const [jobSizeAccepted, setJobSizeAccepted] = useState([]);
  const [availability, setAvailability] = useState([]);  // Changed to array for multi-select
  const [freeEstimates, setFreeEstimates] = useState(false);
  const [reviewPlatform, setReviewPlatform] = useState("");  // Single select
  const [reviewLink, setReviewLink] = useState("");  // Required if platform selected
  const [workPhotosUrl, setWorkPhotosUrl] = useState("");
  const [preferredContactMethod, setPreferredContactMethod] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [phone, setPhone] = useState("");
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    checkOnboardingStatus();
  }, []);

  const checkOnboardingStatus = async () => {
    try {
      const response = await axios.get(`${API}/contractor/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.onboarding_complete) {
        navigate("/contractor");
        return;
      }
      
      // Load existing profile data
      const profile = response.data.profile;
      if (profile) {
        // Page 1 data
        if (profile.skills) setSkills(profile.skills);
        if (profile.other_skill_description) setOtherSkillDescription(profile.other_skill_description);
        if (profile.years_experience) setYearsExperience(profile.years_experience);
        
        // Page 2 data
        if (profile.is_licensed) setIsLicensed(profile.is_licensed);
        if (profile.is_insured !== undefined) setIsInsured(profile.is_insured);
        if (profile.insurance_proof_url) setInsuranceProofUrl(profile.insurance_proof_url);
        if (profile.service_area) setServiceArea(profile.service_area);
        if (profile.business_type) setBusinessType(profile.business_type);
        
        // Page 3 data
        if (profile.job_size_accepted) setJobSizeAccepted(profile.job_size_accepted);
        if (profile.availability) setAvailability(Array.isArray(profile.availability) ? profile.availability : [profile.availability]);
        if (profile.free_estimates !== undefined) setFreeEstimates(profile.free_estimates);
        if (profile.review_platform) setReviewPlatform(profile.review_platform);
        if (profile.review_link) setReviewLink(profile.review_link);
        if (profile.work_photos_url) setWorkPhotosUrl(profile.work_photos_url);
        if (profile.preferred_contact_method) setPreferredContactMethod(profile.preferred_contact_method);
        if (profile.sms_opt_in !== undefined) setSmsOptIn(profile.sms_opt_in);
        if (profile.phone) setPhone(profile.phone);
      }
      
      setCurrentPage(response.data.current_page || 1);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  };

  const validatePage1 = () => {
    const newErrors = {};
    if (skills.length === 0) {
      newErrors.skills = "Please select at least one skill";
    }
    if (skills.includes("Other") && !otherSkillDescription.trim()) {
      newErrors.otherSkillDescription = "Please describe your other skill";
    }
    if (!yearsExperience) {
      newErrors.yearsExperience = "Please select years of experience";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePage2 = () => {
    const newErrors = {};
    if (!isLicensed) {
      newErrors.isLicensed = "Please select your license status";
    }
    if (!serviceArea.trim()) {
      newErrors.serviceArea = "Please enter your service area";
    }
    if (!businessType) {
      newErrors.businessType = "Please select your business type";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validatePage3 = () => {
    const newErrors = {};
    if (jobSizeAccepted.length === 0) {
      newErrors.jobSizeAccepted = "Please select at least one job size";
    }
    if (availability.length === 0) {
      newErrors.availability = "Please select at least one availability option";
    }
    if (!preferredContactMethod) {
      newErrors.preferredContactMethod = "Please select your preferred contact method";
    }
    // Validate review link is required if platform selected
    if (reviewPlatform && reviewPlatform !== "No online reviews yet") {
      if (!reviewLink.trim()) {
        newErrors.reviewLink = "Review link is required when a platform is selected";
      } else if (!reviewLink.includes("http") && !reviewLink.includes("www")) {
        newErrors.reviewLink = "Please enter a valid URL (must include http or www)";
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSkillToggle = (skill) => {
    setSkills(prev => {
      if (prev.includes(skill)) {
        return prev.filter(s => s !== skill);
      } else {
        return [...prev, skill];
      }
    });
    if (errors.skills) setErrors(prev => ({ ...prev, skills: null }));
  };

  const handleAvailabilityToggle = (option) => {
    setAvailability(prev => {
      if (prev.includes(option)) {
        return prev.filter(o => o !== option);
      } else {
        return [...prev, option];
      }
    });
    if (errors.availability) setErrors(prev => ({ ...prev, availability: null }));
  };

  const handleJobSizeToggle = (size) => {
    setJobSizeAccepted(prev => {
      if (prev.includes(size)) {
        return prev.filter(s => s !== size);
      } else {
        return [...prev, size];
      }
    });
    if (errors.jobSizeAccepted) setErrors(prev => ({ ...prev, jobSizeAccepted: null }));
  };

  const savePage1 = async () => {
    if (!validatePage1()) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/contractor/onboarding/page1`, {
        skills,
        other_skill_description: otherSkillDescription || null,
        years_experience: yearsExperience
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentPage(2);
      setErrors({});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const savePage2 = async () => {
    if (!validatePage2()) return;
    
    setSaving(true);
    try {
      await axios.post(`${API}/contractor/onboarding/page2`, {
        is_licensed: isLicensed,
        is_insured: isInsured,
        insurance_proof_url: insuranceProofUrl || null,
        service_area: serviceArea,
        business_type: businessType
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentPage(3);
      setErrors({});
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const savePage3 = async () => {
    if (!validatePage3()) return;
    
    setSaving(true);
    try {
      const response = await axios.post(`${API}/contractor/onboarding/page3`, {
        job_size_accepted: jobSizeAccepted,
        availability: availability,
        free_estimates: freeEstimates,
        review_platform: reviewPlatform || null,
        review_link: reviewLink || null,
        work_photos_url: workPhotosUrl || null,
        preferred_contact_method: preferredContactMethod,
        sms_opt_in: smsOptIn,
        phone: phone || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(response.data.message);
      navigate("/contractor");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const getProgressPercent = () => {
    switch (currentPage) {
      case 1: return 35;
      case 2: return 70;
      case 3: return 100;
      default: return 0;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-[#0FA3B1] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Progress Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm text-[#1F2933]/60">Step {currentPage} of 3</span>
            <span className="text-sm font-medium text-[#0FA3B1]">{getProgressPercent()}% Complete</span>
          </div>
          <Progress value={getProgressPercent()} className="h-2" />
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Page 1: Skills & Experience */}
        {currentPage === 1 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-[#0FA3B1]/10 flex items-center justify-center mx-auto mb-4">
                <Wrench className="w-8 h-8 text-[#0FA3B1]" />
              </div>
              <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] mb-2">
                Tell us what you do
              </h1>
              <p className="text-[#1F2933]/60">
                This helps us match you with the right jobs
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6">
              {/* Skills */}
              <div>
                <Label className="text-[#1F2933] font-medium">
                  Services / Skills <span className="text-red-500">*</span>
                </Label>
                <p className="text-sm text-[#1F2933]/50 mb-3">Select all that apply</p>
                <div className={`grid grid-cols-2 gap-3 p-4 border rounded-xl ${errors.skills ? 'border-red-500' : 'border-gray-200'}`}>
                  {SKILLS_OPTIONS.map(skill => (
                    <div key={skill} className="flex items-center gap-3">
                      <Checkbox
                        id={`skill-${skill}`}
                        checked={skills.includes(skill)}
                        onCheckedChange={() => handleSkillToggle(skill)}
                      />
                      <label 
                        htmlFor={`skill-${skill}`}
                        className="text-sm text-[#1F2933] cursor-pointer"
                      >
                        {skill}
                      </label>
                    </div>
                  ))}
                </div>
                {errors.skills && (
                  <p className="text-red-500 text-sm mt-1">{errors.skills}</p>
                )}
              </div>

              {/* Other skill description */}
              {skills.includes("Other") && (
                <div className="animate-fade-in">
                  <Label htmlFor="otherSkill" className="text-[#1F2933] font-medium">
                    Other (please describe) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="otherSkill"
                    value={otherSkillDescription}
                    onChange={(e) => {
                      setOtherSkillDescription(e.target.value);
                      if (errors.otherSkillDescription) setErrors(prev => ({ ...prev, otherSkillDescription: null }));
                    }}
                    placeholder="Describe your other skills"
                    className={`mt-2 ${errors.otherSkillDescription ? 'border-red-500' : ''}`}
                  />
                  {errors.otherSkillDescription && (
                    <p className="text-red-500 text-sm mt-1">{errors.otherSkillDescription}</p>
                  )}
                </div>
              )}

              {/* Years of experience */}
              <div>
                <Label className="text-[#1F2933] font-medium">
                  Years of experience <span className="text-red-500">*</span>
                </Label>
                <Select value={yearsExperience} onValueChange={(v) => {
                  setYearsExperience(v);
                  if (errors.yearsExperience) setErrors(prev => ({ ...prev, yearsExperience: null }));
                }}>
                  <SelectTrigger className={`mt-2 ${errors.yearsExperience ? 'border-red-500' : ''}`}>
                    <SelectValue placeholder="Select experience" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_OPTIONS.map(exp => (
                      <SelectItem key={exp} value={exp}>{exp}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.yearsExperience && (
                  <p className="text-red-500 text-sm mt-1">{errors.yearsExperience}</p>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="mt-8 flex justify-end">
              <Button
                onClick={savePage1}
                disabled={saving}
                className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white px-8"
              >
                {saving ? "Saving..." : "Next"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Page 2: Compliance & Service Area */}
        {currentPage === 2 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-[#0FA3B1]/10 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#0FA3B1]" />
              </div>
              <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] mb-2">
                Licensing, insurance, and service area
              </h1>
              <p className="text-[#1F2933]/60">
                We use this to assign the right jobs
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6">
              {/* Licensed */}
              <div>
                <Label className="text-[#1F2933] font-medium">
                  Are you licensed to work in your trade? <span className="text-red-500">*</span>
                </Label>
                <RadioGroup
                  value={isLicensed}
                  onValueChange={(v) => {
                    setIsLicensed(v);
                    if (errors.isLicensed) setErrors(prev => ({ ...prev, isLicensed: null }));
                  }}
                  className={`mt-3 space-y-2 ${errors.isLicensed ? 'ring-2 ring-red-500 rounded-xl p-2' : ''}`}
                >
                  {LICENSE_OPTIONS.map(option => (
                    <div key={option.value} className="flex items-center gap-3">
                      <RadioGroupItem value={option.value} id={`license-${option.value}`} />
                      <label htmlFor={`license-${option.value}`} className="text-[#1F2933] cursor-pointer">
                        {option.label}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
                {errors.isLicensed && (
                  <p className="text-red-500 text-sm mt-1">{errors.isLicensed}</p>
                )}
              </div>

              {/* Insured */}
              <div>
                <Label className="text-[#1F2933] font-medium">
                  Are you insured? <span className="text-red-500">*</span>
                </Label>
                <RadioGroup
                  value={isInsured ? "yes" : "no"}
                  onValueChange={(v) => setIsInsured(v === "yes")}
                  className="mt-3 flex gap-6"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="yes" id="insured-yes" />
                    <label htmlFor="insured-yes" className="text-[#1F2933] cursor-pointer">Yes</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="no" id="insured-no" />
                    <label htmlFor="insured-no" className="text-[#1F2933] cursor-pointer">No</label>
                  </div>
                </RadioGroup>
              </div>

              {/* Insurance proof upload (if insured) */}
              {isInsured && (
                <div className="animate-fade-in">
                  <Label className="text-[#1F2933] font-medium">
                    Upload proof of insurance <span className="text-[#1F2933]/50">(optional)</span>
                  </Label>
                  <div className="mt-2 border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-[#1F2933]/60">
                      Drag & drop or click to upload
                    </p>
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      className="mt-2 opacity-0 h-0"
                      onChange={(e) => {
                        // In production, this would upload to cloud storage
                        if (e.target.files?.[0]) {
                          setInsuranceProofUrl(`uploaded_${e.target.files[0].name}`);
                          toast.success("File selected (upload simulated)");
                        }
                      }}
                    />
                    {insuranceProofUrl && (
                      <p className="text-sm text-green-600 mt-2 flex items-center justify-center gap-1">
                        <Check className="w-4 h-4" /> File uploaded
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Service area */}
              <div>
                <Label htmlFor="serviceArea" className="text-[#1F2933] font-medium">
                  Service area <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  id="serviceArea"
                  value={serviceArea}
                  onChange={(e) => {
                    setServiceArea(e.target.value);
                    if (errors.serviceArea) setErrors(prev => ({ ...prev, serviceArea: null }));
                  }}
                  placeholder="Enter ZIP codes, cities, or areas you serve"
                  className={`mt-2 min-h-[100px] ${errors.serviceArea ? 'border-red-500' : ''}`}
                />
                {errors.serviceArea && (
                  <p className="text-red-500 text-sm mt-1">{errors.serviceArea}</p>
                )}
              </div>

              {/* Business type */}
              <div>
                <Label className="text-[#1F2933] font-medium">
                  Business type <span className="text-red-500">*</span>
                </Label>
                <RadioGroup
                  value={businessType}
                  onValueChange={(v) => {
                    setBusinessType(v);
                    if (errors.businessType) setErrors(prev => ({ ...prev, businessType: null }));
                  }}
                  className={`mt-3 space-y-2 ${errors.businessType ? 'ring-2 ring-red-500 rounded-xl p-2' : ''}`}
                >
                  {BUSINESS_TYPE_OPTIONS.map(type => (
                    <div key={type} className="flex items-center gap-3">
                      <RadioGroupItem value={type} id={`business-${type}`} />
                      <label htmlFor={`business-${type}`} className="text-[#1F2933] cursor-pointer">
                        {type}
                      </label>
                    </div>
                  ))}
                </RadioGroup>
                {errors.businessType && (
                  <p className="text-red-500 text-sm mt-1">{errors.businessType}</p>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="mt-8 flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(1)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={savePage2}
                disabled={saving}
                className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white px-8"
              >
                {saving ? "Saving..." : "Next"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Page 3: Preferences & Proof */}
        {currentPage === 3 && (
          <div className="animate-fade-in">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-[#0FA3B1]/10 flex items-center justify-center mx-auto mb-4">
                <Settings className="w-8 h-8 text-[#0FA3B1]" />
              </div>
              <h1 className="font-manrope font-bold text-2xl sm:text-3xl text-[#1F2933] mb-2">
                Job preferences
              </h1>
              <p className="text-[#1F2933]/60">
                Optional details help you win more jobs
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-6 space-y-6">
              {/* Job sizes */}
              <div>
                <Label className="text-[#1F2933] font-medium">
                  What job sizes do you accept? <span className="text-red-500">*</span>
                </Label>
                <p className="text-sm text-[#1F2933]/50 mb-3">Select all that apply</p>
                <div className={`grid grid-cols-2 gap-3 ${errors.jobSizeAccepted ? 'ring-2 ring-red-500 rounded-xl p-2' : ''}`}>
                  {JOB_SIZE_OPTIONS.map(size => (
                    <button
                      key={size}
                      onClick={() => handleJobSizeToggle(size)}
                      className={`p-3 border rounded-xl text-sm font-medium transition-all flex items-center gap-2 ${
                        jobSizeAccepted.includes(size)
                          ? 'bg-[#0FA3B1]/10 border-[#0FA3B1] text-[#0FA3B1]'
                          : 'border-gray-200 hover:border-[#0FA3B1]/50'
                      }`}
                    >
                      <DollarSign className="w-4 h-4" />
                      {size}
                    </button>
                  ))}
                </div>
                {errors.jobSizeAccepted && (
                  <p className="text-red-500 text-sm mt-1">{errors.jobSizeAccepted}</p>
                )}
              </div>

              {/* Availability - Multi-select */}
              <div>
                <Label className="text-[#1F2933] font-medium">
                  Typical availability (select all that apply) <span className="text-red-500">*</span>
                </Label>
                <div className={`mt-3 space-y-2 ${errors.availability ? 'ring-2 ring-red-500 rounded-xl p-2' : ''}`}>
                  {AVAILABILITY_OPTIONS.map(option => (
                    <div key={option} className="flex items-center gap-3">
                      <Checkbox
                        id={`avail-${option}`}
                        checked={availability.includes(option)}
                        onCheckedChange={() => handleAvailabilityToggle(option)}
                      />
                      <label htmlFor={`avail-${option}`} className="text-[#1F2933] cursor-pointer flex items-center gap-2">
                        <Clock className="w-4 h-4 text-[#1F2933]/50" />
                        {option}
                      </label>
                    </div>
                  ))}
                </div>
                {availability.length > 0 && (
                  <p className="text-sm text-[#0FA3B1] mt-2">{availability.length} option(s) selected</p>
                )}
                {errors.availability && (
                  <p className="text-red-500 text-sm mt-1">{errors.availability}</p>
                )}
              </div>

              {/* Free estimates */}
              <div>
                <Label className="text-[#1F2933] font-medium">
                  Do you offer free estimates? <span className="text-red-500">*</span>
                </Label>
                <RadioGroup
                  value={freeEstimates ? "yes" : "no"}
                  onValueChange={(v) => setFreeEstimates(v === "yes")}
                  className="mt-3 flex gap-6"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="yes" id="estimates-yes" />
                    <label htmlFor="estimates-yes" className="text-[#1F2933] cursor-pointer">Yes</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="no" id="estimates-no" />
                    <label htmlFor="estimates-no" className="text-[#1F2933] cursor-pointer">No</label>
                  </div>
                </RadioGroup>
              </div>

              {/* Quality Boost Section */}
              <div className="border-t border-gray-200 pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Star className="w-5 h-5 text-amber-500" />
                  <span className="font-medium text-[#1F2933]">Quality Boost</span>
                  <span className="text-xs bg-gray-100 text-[#1F2933]/60 px-2 py-1 rounded-full">Optional</span>
                </div>

                {/* Online reviews - Single select */}
                <div className="mb-4">
                  <Label className="text-[#1F2933] font-medium">
                    Do you have online reviews? (Select ONE)
                  </Label>
                  <RadioGroup
                    value={reviewPlatform}
                    onValueChange={(v) => {
                      setReviewPlatform(v);
                      // Clear review link if "No online reviews yet" is selected
                      if (v === "No online reviews yet") {
                        setReviewLink("");
                      }
                      if (errors.reviewLink) setErrors(prev => ({ ...prev, reviewLink: null }));
                    }}
                    className="mt-3 space-y-2"
                  >
                    {REVIEW_PLATFORM_OPTIONS.map(platform => (
                      <div key={platform} className="flex items-center gap-3">
                        <RadioGroupItem value={platform} id={`review-${platform}`} />
                        <label htmlFor={`review-${platform}`} className="text-[#1F2933] cursor-pointer">
                          {platform}
                        </label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {/* Review link - Required if platform selected */}
                {reviewPlatform && reviewPlatform !== "No online reviews yet" && (
                  <div className="mb-4 animate-fade-in">
                    <Label htmlFor="reviewLink" className="text-[#1F2933] font-medium">
                      Review link <span className="text-red-500">*</span>
                    </Label>
                    <p className="text-sm text-[#1F2933]/50 mb-2">Paste the link to your {reviewPlatform} profile</p>
                    <Input
                      id="reviewLink"
                      value={reviewLink}
                      onChange={(e) => {
                        setReviewLink(e.target.value);
                        if (errors.reviewLink) setErrors(prev => ({ ...prev, reviewLink: null }));
                      }}
                      placeholder={`https://www.${reviewPlatform.toLowerCase()}.com/...`}
                      className={`${errors.reviewLink ? 'border-red-500 ring-1 ring-red-500' : ''}`}
                    />
                    {errors.reviewLink && (
                      <p className="text-red-500 text-sm mt-1">{errors.reviewLink}</p>
                    )}
                  </div>
                )}

                {/* Work photos upload */}
                <div>
                  <Label className="text-[#1F2933] font-medium">
                    Upload examples of your work <span className="text-[#1F2933]/50">(optional)</span>
                  </Label>
                  <div className="mt-2 border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                    <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-[#1F2933]/60">
                      Upload photos of your work
                    </p>
                    <Input
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      multiple
                      className="mt-2 opacity-0 h-0"
                      onChange={(e) => {
                        if (e.target.files?.length) {
                          setWorkPhotosUrl(`uploaded_${e.target.files.length}_photos`);
                          toast.success(`${e.target.files.length} photo(s) selected`);
                        }
                      }}
                    />
                    {workPhotosUrl && (
                      <p className="text-sm text-green-600 mt-2 flex items-center justify-center gap-1">
                        <Check className="w-4 h-4" /> Photos uploaded
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Preferred contact method */}
              <div className="border-t border-gray-200 pt-6">
                <Label className="text-[#1F2933] font-medium">
                  Preferred contact method <span className="text-red-500">*</span>
                </Label>
                <div className={`mt-3 grid grid-cols-3 gap-3 ${errors.preferredContactMethod ? 'ring-2 ring-red-500 rounded-xl p-2' : ''}`}>
                  {CONTACT_METHOD_OPTIONS.map(option => {
                    const Icon = option.icon;
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          setPreferredContactMethod(option.value);
                          if (errors.preferredContactMethod) setErrors(prev => ({ ...prev, preferredContactMethod: null }));
                        }}
                        className={`p-4 border rounded-xl text-center transition-all ${
                          preferredContactMethod === option.value
                            ? 'bg-[#0FA3B1]/10 border-[#0FA3B1] text-[#0FA3B1]'
                            : 'border-gray-200 hover:border-[#0FA3B1]/50'
                        }`}
                      >
                        <Icon className="w-6 h-6 mx-auto mb-2" />
                        <span className="text-sm font-medium">{option.value}</span>
                      </button>
                    );
                  })}
                </div>
                {errors.preferredContactMethod && (
                  <p className="text-red-500 text-sm mt-1">{errors.preferredContactMethod}</p>
                )}
              </div>

              {/* SMS Notifications Opt-in */}
              <div className="border-t border-gray-200 pt-6">
                <div className="bg-[#0FA3B1]/5 rounded-xl p-4 border border-[#0FA3B1]/20">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="sms-opt-in"
                      checked={smsOptIn}
                      onCheckedChange={(checked) => setSmsOptIn(checked === true)}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <label htmlFor="sms-opt-in" className="text-[#1F2933] font-medium cursor-pointer block">
                        Text me when new matching leads are available
                      </label>
                      <p className="text-sm text-[#1F2933]/60 mt-1">
                        Get instant SMS alerts for leads matching your skills and service area.
                      </p>
                      <p className="text-xs text-[#1F2933]/40 mt-2">
                        Msg & data rates may apply. Reply STOP to unsubscribe.
                      </p>
                    </div>
                  </div>
                  
                  {smsOptIn && (
                    <div className="mt-4 animate-fade-in">
                      <Label htmlFor="phone-sms" className="text-[#1F2933] font-medium">
                        Mobile phone number
                      </Label>
                      <Input
                        id="phone-sms"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="(512) 555-0123"
                        className="mt-2"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="mt-8 flex justify-between">
              <Button
                variant="outline"
                onClick={() => setCurrentPage(2)}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <Button
                onClick={savePage3}
                disabled={saving}
                className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white px-8"
              >
                {saving ? "Finishing..." : "Finish Setup"}
                <Check className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
